<script>
import appConfig from '@src/app.config.json'
import Layout from '@layouts/user-management.vue'
import { hasErrors } from '@utils/form'
import { httpServices } from '@utils/http-services'
import { permissionsService } from '@utils/permissions'
import { features } from '@utils/features.const'
import { passwordStrength } from 'check-password-strength'

export default {
  page: {
    title: 'Create User',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout },
  data() {
    return {
      hasErrors,
      formLayout: 'horizontal',
      orgs: null,
      features: [],
      form: this.$form.createForm(this, { name: 'coordinated' }),
    }
  },
  mounted() {
    this.getOrgs()
  },
  methods: {
    getOrgs() {
      if (permissionsService.isVanishedAdmin()) {
        httpServices.getOrgs().then((res) => {
          this.orgs = res
          this.features = features.map((i) => i.id)
        })
      } else {
        httpServices
          .getOrganization(permissionsService.loggedInUserOrg())
          .then((org) => {
            this.orgs = [org]
            this.features = org.permissions.features
          })
      }
    },
    handleSubmit(e) {
      e.preventDefault()
      this.form.validateFields((err, values) => {
        if (!err) {
          const orgId = values.orgId.replace('ORG#', '')
          const data = {
            orgId,
            userId: values.email,
            meta: {
              name: values.first + ' ' + values.last,
              email: values.email,
            },
            permissions: {
              role: values.role,
              organization:
                this.orgs.filter((i) => i.pk === orgId)[0]?.meta?.name || orgId,
              features: values.features || [],
            },
          }

          if (passwordStrength(values.password).value !== 'Strong') {
            this.$notification.error({
              message: 'Failed to Create User: Password not strong enough.',
            })
            return
          }

          const p = {
            first_name: values.first,
            last_name: values.last,
            email: values.email,
            password: values.password,
            username: values.email,
          }

          httpServices.createAuth0User(p).then(
            (m) => {
              data.meta.user_id = m?.user_id

              httpServices.createUser(data).then(
                (res) => {
                  this.$notification.success({
                    message: `${
                      res?.data?.meta?.user || 'User'
                    } Successfully Created`,
                  })
                  this.$router.push({
                    name: 'user-management',
                  })
                },
                () => {
                  this.$notification.error({
                    message: 'Failed to Create User: That ID already exists.',
                  })
                }
              )
            },
            (_e) => {
              this.$notification.error({
                message: 'Failed to Create User: That Auth ID already exists.',
              })
            }
          )
        } else {
          this.$notification.warning({
            message: `There are errors in your form, please update required fields.`,
          })
        }
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div class="ant-row profile--container">
      <div class="breadcrumb--container">
        <a-breadcrumb>
          <a-breadcrumb-item href="#">
            <router-link to="/">
              <a-icon type="home" />
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item href="#">
            <router-link to="/user-management">
              <a-icon type="team" />
              <span>User Management</span>
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>
            <a-icon type="plus-square" />
            Add New User
          </a-breadcrumb-item>
        </a-breadcrumb>
      </div>
      <div class="form-container">
        <div
          class="header"
          style="
            padding: 1.2rem;
            margin-bottom: 0;
            text-align: center;
            background: #141414;
            border-radius: 4px 4px 0 0;
          "
        >
          <h4 style="margin: 0; font-weight: 100; color: #fff">
            <a-icon type="plus-square" style="padding-right: 4px" />
            Add New User
          </h4>
        </div>

        <template>
          <a-form :form="form" @submit="handleSubmit">
            <a-card>
              <a-tabs default-active-key="1">
                <a-tab-pane key="1" tab="User Data">
                  <a-card style="width: 100%">
                    <h5>User Meta</h5>
                    <a-row>
                      <a-col :sm="16" :md="4">
                        <a-form-item label="First Name">
                          <a-input
                            v-decorator="[
                              'first',
                              {
                                rules: [
                                  {
                                    required: true,
                                    placeholder:
                                      'Please input your First Name!',
                                  },
                                ],
                              },
                            ]"
                          />
                        </a-form-item>
                      </a-col>

                      <a-col :sm="16" :md="4">
                        <a-form-item label="Last Name">
                          <a-input
                            v-decorator="[
                              'last',
                              {
                                rules: [
                                  {
                                    required: true,
                                    placeholder: 'Please input your Last Name!',
                                  },
                                ],
                              },
                            ]"
                          />
                        </a-form-item>
                      </a-col>

                      <a-col :sm="16" :md="8">
                        <a-form-item label="Email">
                          <a-input
                            v-decorator="[
                              'email',
                              {
                                rules: [
                                  {
                                    type: 'email',
                                    message: 'The input is not valid E-mail!',
                                  },
                                  {
                                    required: true,
                                    message: 'Please input your E-mail!',
                                  },
                                ],
                              },
                            ]"
                          />
                        </a-form-item>
                      </a-col>

                      <a-col :sm="16" :md="8">
                        <a-form-item label="Password">
                          <a-input-password
                            v-decorator="[
                              'password',
                              {
                                rules: [
                                  {
                                    required: true,
                                    placeholder: 'Please input your password!',
                                  },
                                ],
                              },
                            ]"
                          />
                        </a-form-item>
                      </a-col>
                    </a-row>

                    <a-row>
                      <h5>User Permissions</h5>
                      <div class="ant-col ant-col-8">
                        <a-card title="Role Type">
                          <a-form-item label="Select a User Role">
                            <a-select
                              v-decorator="[
                                'role',
                                {
                                  rules: [
                                    {
                                      required: true,
                                      placeholder: 'Please input your role!',
                                    },
                                  ],
                                },
                              ]"
                            >
                              <a-select-option value="admin">
                                Admin
                              </a-select-option>
                              <a-select-option value="user">
                                User
                              </a-select-option>
                            </a-select>
                          </a-form-item>
                        </a-card>
                      </div>

                      <div class="ant-col ant-col-8">
                        <a-card title="Role Type">
                          <a-form-item label="Select a User Organization">
                            <a-select
                              v-decorator="[
                                'orgId',
                                {
                                  rules: [
                                    {
                                      required: true,
                                      placeholder: 'Please input your org!',
                                    },
                                  ],
                                },
                              ]"
                            >
                              <a-select-option
                                v-for="(o, i) in orgs"
                                :key="i"
                                :value="o.pk"
                              >
                                {{ o.meta.name }}
                              </a-select-option>
                            </a-select>
                          </a-form-item>
                        </a-card>
                      </div>
                      <div class="ant-col ant-col-8">
                        <a-card title="Features">
                          <a-form-item label="Features">
                            <a-select
                              v-decorator="['features']"
                              mode="multiple"
                            >
                              <a-select-option
                                v-for="(f, i) in features"
                                :key="i + f"
                                :value="f"
                              >
                                {{ f }}
                              </a-select-option>
                            </a-select>
                          </a-form-item>
                        </a-card>
                      </div>
                    </a-row>
                  </a-card>
                </a-tab-pane>
              </a-tabs>

              <a-row class="submit--container">
                <a-col :sm="16" :md="8">
                  <a-form-item>
                    <a-button
                      type="primary"
                      html-type="submit"
                      :disabled="hasErrors(form.getFieldsError())"
                    >
                      Save
                    </a-button>
                  </a-form-item>
                </a-col>
                <a-col :sm="16" :md="8">
                  <a-form-item>
                    <a-button @click="$router.go(-1)"> Cancel</a-button>
                  </a-form-item>
                </a-col>
              </a-row>
            </a-card>
          </a-form>
        </template>
      </div>
    </div>
  </Layout>
</template>
